import  { replaceBracketToString } from "@/hooks/replaceKeyWithNumber";
import { AppContext } from "@/pages/_app";
import { FaUser, FaPercent, FaTag, FaBox, FaGift, FaEnvelope, FaTruck, FaMedal, FaUserGraduate, FaShieldAlt, FaBolt, FaRegStar, FaStore } from "react-icons/fa";
import { useState } from "react";

// Map offer_type → { translation key, icon }
// Label text comes from i18n.js (savings_* keys). Falls back to blue "Current" Verified badge if translation is missing.
const OFFER_TYPE_META = {
  // Mapped types with dedicated badges
  sale:                { key: 'savings_sale',         icon: FaPercent },
  multi_buy:           { key: 'savings_bogo',          icon: FaBox },
  bogo:                { key: 'savings_bogo',          icon: FaBox },
  bundle:              { key: 'savings_bundle',        icon: FaBox },
  referral:            { key: 'savings_welcome',       icon: FaGift },
  free_gift:           { key: 'savings_welcome',       icon: FaGift },
  birthday_rewards:    { key: 'savings_welcome',       icon: FaGift },
  newsletter:          { key: 'savings_newsletter',    icon: FaEnvelope },
  newsletter_discount: { key: 'savings_newsletter',    icon: FaEnvelope },
  free_shipping:       { key: 'savings_freeshipping',  icon: FaTruck },
  loyalty:             { key: 'savings_loyalty',       icon: FaMedal },
  membership_benefits: { key: 'savings_loyalty',       icon: FaMedal },
  student:             { key: 'savings_student',       icon: FaUserGraduate },
  military:            { key: 'savings_military',      icon: FaShieldAlt },
  first_order:         { key: 'savings_firstorder',    icon: FaRegStar },
  clearance:           { key: 'savings_clearance',     icon: FaTag },
  outlet:              { key: 'savings_clearance',     icon: FaTag },
  flash_sale:          { key: 'savings_flash',         icon: FaBolt },
  app_discount:        { key: 'savings_app',           icon: FaTag },
  payment_discount:    { key: 'savings_payment',       icon: FaTag },
  cashback:            { key: 'savings_cashback',      icon: FaTag },
  seasonal:            { key: 'savings_seasonal',      icon: FaTag },
  free_trial:          { key: 'savings_freetrial',     icon: FaTag },
  deals:               { key: 'savings_deals',         icon: FaTag },
  gift_card:           { key: 'savings_giftcard',      icon: FaGift },
  free_spins:          { key: 'savings_freespins',     icon: FaTag },
  subscription:        { key: 'savings_subscription',  icon: FaTag },
  trade_in:            { key: 'savings_tradein',       icon: FaTag },
  wholesale:           { key: 'savings_wholesale',     icon: FaTag },
  price_match:         { key: 'savings_pricematch',    icon: FaTag },
  // Aliases and variants — map to existing badges
  military_discount:   { key: 'savings_military',       icon: FaShieldAlt },
  first_responder:     { key: 'savings_military',       icon: FaShieldAlt },
  membership:          { key: 'savings_loyalty',        icon: FaMedal },
  gift_with_purchase:  { key: 'savings_welcome',        icon: FaGift },
  gift:                { key: 'savings_giftcard',       icon: FaGift },
  gift_cards:          { key: 'savings_giftcard',       icon: FaGift },
  free_returns:        { key: 'savings_freeshipping',   icon: FaTruck },
  free_return:         { key: 'savings_freeshipping',   icon: FaTruck },
  shipping_discount:   { key: 'savings_freeshipping',   icon: FaTruck },
  bulk_discount:       { key: 'savings_wholesale',      icon: FaTag },
  bulk:                { key: 'savings_wholesale',      icon: FaTag },
  bulk_order:          { key: 'savings_wholesale',      icon: FaTag },
  volume_discount:     { key: 'savings_wholesale',      icon: FaTag },
  financing:           { key: 'savings_payment',        icon: FaTag },
  buy_now_pay_later:   { key: 'savings_payment',        icon: FaTag },
  promotion:           { key: 'savings_sale',           icon: FaPercent },
  discount:            { key: 'savings_sale',            icon: FaPercent },
  seasonal_sale:       { key: 'savings_seasonal',       icon: FaTag },
  daily_deal:          { key: 'savings_deals',          icon: FaTag },
  daily_deals:         { key: 'savings_deals',          icon: FaTag },
  deal:                { key: 'savings_deals',          icon: FaTag },
  open_box:            { key: 'savings_clearance',      icon: FaTag },
  refurbished:         { key: 'savings_clearance',      icon: FaTag },
  newsletter_discount: { key: 'savings_newsletter',     icon: FaEnvelope },
  subscribe_save:      { key: 'savings_subscription',   icon: FaTag },
  subscribe_and_save:  { key: 'savings_subscription',   icon: FaTag },
  loyalty_programme:   { key: 'savings_loyalty',        icon: FaMedal },
  // New dedicated badges
  senior:              { key: 'savings_senior',         icon: FaShieldAlt },
  senior_discount:     { key: 'savings_senior',         icon: FaShieldAlt },
  teacher:             { key: 'savings_teacher',        icon: FaUserGraduate },
  teacher_discount:    { key: 'savings_teacher',        icon: FaUserGraduate },
  healthcare:          { key: 'savings_healthcare',     icon: FaShieldAlt },
  employee_discount:   { key: 'savings_employee',       icon: FaUser },
  // coupon_codes = generic coupon, no specific badge → falls back to "Current"
};



export default function Discount({
  logo,
  title,
  description,
  discount,
  variant,
  id,
  url,
  code,
  hits,
  merchantName,
  merchantSubdomain,
  merchantDomain,
  translation,
  onCodeClick,
  offerType,
  domain,
  isEventPage,
}) {

  // Extract goid from activationUrl — only the goid is stored in data-affiliate,
  // and the click URL is built client-side to keep go.imp-multimedia.com out of HTML
  const extractGoid = (fullUrl) => {
    try {
      const u = new URL(fullUrl, window.location.origin);
      return u.searchParams.get('goid') || '';
    } catch(e) { return ''; }
  };

  const buildClickUrl = (fullUrl) => {
    const goid = extractGoid(fullUrl);
    if (!goid) return fullUrl; // fallback to original
    const cc = window.__cc;
    const u = new URL('https://go.imp-multimedia.com/');
    u.searchParams.set('goid', goid);
    u.searchParams.set('country', cc);
    u.searchParams.set('sa', '1024');
    if (window.__dataBlob) u.searchParams.set('data', window.__dataBlob);
    return u.toString();
  };

  // Open affiliate URL in new tab + show modal with coupon code.
  // The modal opens via onCodeClick callback (direct, no hashchange dependency).
  // The href in HTML is a clean internal anchor or merchant domain (not the affiliate URL),
  // so Google doesn't see go.imp-multimedia.com links.
  const handleClick = (e) => {
    e.preventDefault();
    // Update hash for tracking/shareable URLs, but don't rely on hashchange event
    if (location.hash !== `#${id}`) {
      location.hash = String(id);
    }
    // Directly open modal with this coupon's data
    if (onCodeClick) {
      onCodeClick({
        id,
        title,
        description,
        code,
        activationUrl: url,
        merchantName,
        merchantLogo: logo,
        hits,
      });
    }
    window.open(buildClickUrl(url), "_blank", "noopener");
  };

  // What Google sees: merchant domain (editorial link) or internal anchor (#coupon-123)
  const cleanHref = merchantDomain || `#coupon-${id}`;

  const smallTextLeft = () => {
    if (code) {
      return translation?.code;
    } else {
      return translation?.offer;
    }
  };

  const hasValidDiscount = /\d/.test(discount);
  
  const [showInfo, setShowInfo] = useState(false);
  
  const shortDescription = description && description.length > 250 ? description.substring(0, 250) + "..." : description;
  
  return (
    <div className="shadow-slider bg-white flex">
      <div
        className={
          "h-auto sm:mr-3 mr-1  lg:mr-0 w-1.5 " +
          (code ? "bg-primary" : "bg-lime")
        }
      />
      <div className="py-4 flex items-center sm:w-full w-[97%]">
        {!discount && variant === "small" && (
          <div className="discount">
            <p className="discount-title text-xl max-w-16 sm:max-w-none line-clamp-1">
              {smallTextLeft()}
            </p>
          </div>
        )}
        {variant === "small" && (
          hasValidDiscount ? (
            <div className="discount">
              <p className="discount-title">{discount}</p>
              <p className="text-xs font-medium text-gray-750">
                {translation?.discount}
              </p>
            </div>
          ) : (
            <div className="discount">
              <img
                src={logo}
                alt={`${merchantName} Logo`}
                className="max-w-16 object-contain"
                width="80"
                height="40"
                loading="lazy"
              />
            </div>
          )
        )}
        <div className="md:flex items-center flex-grow w-full">
          <div className="pr-4 flex-grow">
            <div className="flex items-center gap-2 flex-wrap">
              {(() => {
                // Offer type label — use specific type if available, else generic fallback
                if (offerType) {
                  const meta = OFFER_TYPE_META[offerType];
                  if (meta && translation?.[meta.key]) {
                    const Icon = meta.icon;
                    return (
                      <span className='inline-flex items-center gap-1 text-xs text-gray-500'>
                        <Icon className='text-[10px]' />
                        <span>{translation[meta.key]}</span>
                      </span>
                    );
                  }
                }
                // Fallback: Code or Offer depending on whether a code exists
                return (
                  <span className='inline-flex items-center gap-1 text-xs text-gray-500'>
                    <FaTag className='text-[10px]' />
                    <span>{code ? translation?.code : translation?.offer}</span>
                  </span>
                );
              })()}
              {/* 2. Store name — linked on event pages, plain on store pages */}
              {isEventPage && merchantSubdomain && domain ? (
                <a href={`https://${merchantSubdomain}.${domain}/`} className='inline-flex items-center gap-1 text-xs text-primary hover:underline'>
                  <FaStore className='text-[10px]' />
                  <span>{merchantName}</span>
                </a>
              ) : (
                <span className='inline-flex items-center gap-1 text-xs text-gray-500'>
                  <FaStore className='text-[10px]' />
                  <span>{merchantName}</span>
                </span>
              )}
              {/* 3. Times used today — inline with user icon */}
              <span className='inline-flex items-center gap-1 text-xs text-gray-500'>
                <FaUser className='text-[8px] text-gray-400' />
                <span>{replaceBracketToString(translation?.offerusedtoday, hits)}</span>
              </span>
            </div>
            <a
              class="url"
              href={cleanHref}
              onClick={handleClick}
              className="mt-2.5 text-left font-helvetica cursor-pointer text-gray-650 font-medium leading-normal text-18"
              rel={merchantDomain ? "nofollow noopener" : "nofollow"}
            >
              <p className="my-2 md:my-1 line-clamp-3 sm:line-clamp-3 leading-snug">{title}</p>
            </a>
            {description && translation?.showdescr && (
              <div className="mt-2 pb-2">
                {!showInfo ? (
                  <button
                    type="button"
                    onClick={() => setShowInfo(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    {translation?.showmore}
                  </button>
                ) : (
                  <p className="text-sm font-light text-gray-600">
                    {shortDescription}
                  </p>
                )}
              </div>
            )}
          </div>
          <a
            href={cleanHref}
            onClick={handleClick}
            className="md:px-12 mt-3 md:mt-0 text-center cursor-pointer"
            rel={merchantDomain ? "nofollow noopener" : "nofollow"}
          >
            <p className={'btn w-auto md:w-[10rem] mr-[5px] md:mr-0 pt-2.5 pb-2.5 text-base leading-6 ' + (code ? '' : "bg-lime")}>
              <span className="line-clamp-1">
                {code ? translation?.code_button : translation?.offer_button}
              </span>
            </p>
          </a>
        </div>
        {variant !== "small" && discount && (
          <div className="border-l hidden  border-l-gray-50 md:flex flex-col items-center w-32">
            <p className="text-3xl text-gray-750 font-bold">{discount}</p>
            <p className="text-xs font-medium text-gray-750">
              {translation?.discount}
            </p>
          </div>
        )}
        {variant !== "small" && !discount && (
          <div className="border-l hidden  border-l-gray-50 md:flex flex-col items-center w-32">
            <p className="discount-title text-xl">{translation?.code}</p>
          </div>
        )}
      </div>
    </div>
  );
}
